import { computed, reactive } from 'vue';
import { iframeDataBridge } from './iframeDataBridge';
import { session } from '@/utils/auth/sessionService';
import { resolveSeasonalTheme, seasonalThemeSettings } from '@/utils/domain/seasonalTheme';

// The full-screen loaders of the legacy application are Vue (#23635): Main.js waiterHelper draws nothing in the iframe
// any more, it drives EcosShelfProgress.vue over the whole app (App.vue) through window.__vueLoader. Installed once on
// this window, like __vueConnectivity, so a reloaded legacy document finds it from its first script. Two kinds of
// requests, any one shows the loader, the page block wins while both run:
//   - the page block, waiterHelper.blockPage / updateProgress / unblockPage: block({ progress }), progress(pct, src),
//     unblock(). With `progress` (the main synchronization is running) the shelf counts from 0 %, otherwise it waits
//     without a percentage (mass appointments, contracts, the visit save until its synchronization starts). Only the
//     main synchronization (SALFO) moves it: the other progress bars (DCRFO, widgets) also reach updateProgress. The
//     advance never goes back except a reset to 0 when a new synchronization starts (RG-06); at 100 % the shelf first
//     shows "Synchronized", its `done` event then lets it go (RG-07);
//   - named waits, waiterHelper.startWaiting / waitingProgress / stopWaiting: startWait(key), waitProgress(key, pct),
//     stopWait(key). `view` is greeterVM.startLoading / stopLoading (agenda, catalog, cart, filters...), `kpis` the KPI
//     prefetch of the POS and planning lists (appointment.js preloadKpisForList), which gives its percentage. A key only
//     ends its own wait; "Cancel", offered after 10 s, drops them all, like the legacy one only hid its veil.
// A loader without a percentage shows the full shelf, only its board moves.
// The seasonal theme is chosen when the loader opens (RG-25), from the device date and the cached client options:
// those of the legacy document, refreshed by every synchronization, else the session's at application launch.

export const MAIN_SYNC = 'SALFO';
export const VIEW_WAIT = 'view';
export const CANCEL_DELAY_MS = 10000;

const state = reactive({ blocked: false, value: null, waits: {}, cancellable: false, theme: null });

const waiting = computed(() => Object.keys(state.waits).length > 0);
const waitValue = computed(() => {
	const values = Object.values(state.waits).filter((value) => value !== null);
	return values.length ? Math.min(...values) : null;
});

export const legacyLoader = reactive({
	visible: computed(() => state.blocked || waiting.value),
	mode: computed(() => (state.blocked ? 'block' : 'wait')),
	value: computed(() => (state.blocked ? state.value : waitValue.value)),
	theme: computed(() => state.theme),
	cancellable: computed(() => !state.blocked && waiting.value && state.cancellable),
});

let closing = false;
let doneShown = false;
let cancelTimer = null;

const clamp = (pct) => Math.min(100, Math.max(0, Number(pct) || 0));

function cachedOptions() {
	const legacy = iframeDataBridge.getIframeWindow()?.externis?.ecosVM?.options;
	return Array.isArray(legacy) && legacy.length ? legacy : session.options;
}

function open() {
	if (!legacyLoader.visible) state.theme = resolveSeasonalTheme(seasonalThemeSettings(cachedOptions()));
}

function restart(value) {
	closing = false;
	doneShown = false;
	state.value = value;
}

function release() {
	closing = false;
	doneShown = false;
	state.blocked = false;
}

export function blockLegacyPage({ progress = false } = {}) {
	open();
	if (!state.blocked || closing || doneShown) restart(progress ? 0 : null);
	else if (progress && state.value === null) state.value = 0;
	state.blocked = true;
}

export function updateLegacyProgress(pct, src) {
	if (!state.blocked || (src && src !== MAIN_SYNC)) return;
	const value = clamp(pct);
	if (value === 0) restart(0);
	else state.value = Math.max(state.value ?? 0, value);
}

export function unblockLegacyPage() {
	if (!state.blocked) return;
	if (state.value >= 100 && !doneShown) closing = true;
	else release();
}

// EcosShelfProgress `done`: the "Synchronized" state has been shown.
export function legacyLoaderDone() {
	doneShown = true;
	if (closing) release();
}

export function startLegacyWait(key = VIEW_WAIT) {
	if (Object.hasOwn(state.waits, key)) return;
	open();
	if (!waiting.value) {
		clearTimeout(cancelTimer);
		state.cancellable = false;
		cancelTimer = setTimeout(() => (state.cancellable = true), CANCEL_DELAY_MS);
	}
	state.waits[key] = null;
}

export function updateLegacyWait(key, pct) {
	if (!Object.hasOwn(state.waits, key)) return;
	state.waits[key] = Math.max(state.waits[key] ?? 0, clamp(pct));
}

export function stopLegacyWait(key = VIEW_WAIT) {
	delete state.waits[key];
	if (waiting.value) return;
	clearTimeout(cancelTimer);
	state.cancellable = false;
}

// The "Cancel" button: the work goes on, only the loader goes away.
export function cancelLegacyWaits() {
	Object.keys(state.waits).forEach((key) => stopLegacyWait(key));
}

// A reloaded legacy document will not end what the previous one started.
export function detachLegacyLoader() {
	release();
	cancelLegacyWaits();
}

// main.js, before the iframe exists.
export function installLegacyLoaderHost(target = window) {
	target.__vueLoader = { block: blockLegacyPage, progress: updateLegacyProgress, unblock: unblockLegacyPage, startWait: startLegacyWait, waitProgress: updateLegacyWait, stopWait: stopLegacyWait };
}
